<!-- 商家端 - 员工管理 -->
<template>
  <ContentWrap title="员工管理">
    <el-tabs v-model="activeTab">
      <el-tab-pane label="子账号" name="account" />
      <el-tab-pane label="角色管理" name="role" />
    </el-tabs>

    <!-- 子账号 -->
    <template v-if="activeTab === 'account'">
      <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="68px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="queryParams.username" class="!w-200px" clearable placeholder="请输入用户名" @keyup.enter="handleQuery" />
        </el-form-item>
        <el-form-item>
          <el-button @click="handleQuery"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
          <el-button @click="resetQuery"><Icon class="mr-5px" icon="ep:refresh" />重置</el-button>
          <el-button plain type="primary" @click="openForm()">
            <Icon class="mr-5px" icon="ep:plus" />新增子账号
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe class="mt-12px">
        <el-table-column label="用户名" min-width="120" prop="username" />
        <el-table-column label="真实姓名" min-width="100" prop="realName" />
        <el-table-column label="手机号" width="130" prop="mobile" />
        <el-table-column label="角色" width="100" prop="roleName" />
        <el-table-column label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="row.status === 0 ? 'success' : 'danger'">{{ row.status === 0 ? '正常' : '禁用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="最后登录" width="170" prop="lastLoginTime" :formatter="dateFormatter" />
        <el-table-column label="创建时间" width="170" prop="createTime" :formatter="dateFormatter" />
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openForm(row.id)">编辑</el-button>
            <el-button link type="danger" @click="handleDelete(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <Pagination v-model:limit="queryParams.pageSize" v-model:page="queryParams.pageNo" :total="total" @pagination="getList" />
    </template>

    <!-- 角色管理 -->
    <RoleManager v-else />
  </ContentWrap>

  <!-- 子账号表单弹窗 -->
  <el-dialog v-model="dialogVisible" :title="formTitle" width="500px" @close="resetForm">
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="90px">
      <el-form-item label="用户名" prop="username">
        <el-input v-model="formData.username" :disabled="!!formData.id" placeholder="请输入登录用户名" />
      </el-form-item>
      <el-form-item v-if="!formData.id" label="密码" prop="password">
        <el-input v-model="formData.password" show-password placeholder="请输入密码（至少6位）" />
      </el-form-item>
      <el-form-item label="真实姓名" prop="realName">
        <el-input v-model="formData.realName" placeholder="请输入真实姓名" />
      </el-form-item>
      <el-form-item label="手机号" prop="mobile">
        <el-input v-model="formData.mobile" placeholder="请输入手机号" />
      </el-form-item>
      <el-form-item label="邮箱" prop="email">
        <el-input v-model="formData.email" placeholder="请输入邮箱" />
      </el-form-item>
      <el-form-item label="角色" prop="roleCode">
        <el-select v-model="formData.roleCode" class="w-full" placeholder="请选择角色">
          <el-option
            v-for="r in roleOptions"
            :key="r.roleCode"
            :label="r.roleName"
            :value="r.roleCode"
          />
        </el-select>
      </el-form-item>
      <el-form-item v-if="!!formData.id" label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio :value="0">正常</el-radio>
          <el-radio :value="1">禁用</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input v-model="formData.remark" type="textarea" :rows="2" placeholder="选填" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import * as SubAccountApi from '@/api/merchant/sub-account'
import * as MerchantRoleApi from '@/api/merchant/role'
import { ContentWrap } from '@/components/ContentWrap'
import { dateFormatter } from '@/utils/formatTime'
import RoleManager from '../role/index.vue'

defineOptions({ name: 'MerchantSubAccount' })

const activeTab = ref('account')
const message = useMessage()
const loading = ref(false)
const submitting = ref(false)
const list = ref([])
const total = ref(0)
const dialogVisible = ref(false)
const formRef = ref()
const roleOptions = ref<{ roleCode: string; roleName: string }[]>([])
const formTitle = computed(() => (formData.id ? '编辑子账号' : '新增子账号'))

const queryParams = reactive({ pageNo: 1, pageSize: 10, username: '' })
const formData = reactive({
  id: undefined as number | undefined,
  username: '', password: '', realName: '', mobile: '', email: '',
  roleCode: '', status: 0, remark: ''
})
const rules = {
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }, { min: 6, message: '密码至少6位' }],
  roleCode: [{ required: true, message: '请选择角色', trigger: 'change' }]
}

const getList = async () => {
  loading.value = true
  try { const data = await SubAccountApi.getSubAccountPage(queryParams); list.value = data.list; total.value = data.total }
  finally { loading.value = false }
}
const handleQuery = () => { queryParams.pageNo = 1; getList() }
const resetQuery = () => { queryParams.username = ''; handleQuery() }

const openForm = async (id?: number) => {
  resetForm()
  // 加载角色选项
  if (roleOptions.value.length === 0) {
    try { roleOptions.value = await MerchantRoleApi.getRoleList() } catch {}
  }
  if (id) { const row = await SubAccountApi.getSubAccount(id); Object.assign(formData, row) }
  dialogVisible.value = true
}
const resetForm = () => {
  formData.id = undefined; formData.username = ''; formData.password = ''
  formData.realName = ''; formData.mobile = ''; formData.email = ''
  formData.roleCode = ''; formData.status = 0; formData.remark = ''
  formRef.value?.resetFields()
}
const handleSubmit = async () => {
  await formRef.value?.validate(); submitting.value = true
  try {
    formData.id ? await SubAccountApi.updateSubAccount(formData) : await SubAccountApi.createSubAccount(formData)
    message.success(formData.id ? '更新成功' : '创建成功'); dialogVisible.value = false; getList()
  } finally { submitting.value = false }
}
const handleDelete = async (id: number) => {
  try { await message.confirm('确认删除该子账号？'); await SubAccountApi.deleteSubAccount(id); message.success('删除成功'); getList() } catch {}
}
onMounted(() => { getList() })
</script>
